import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { company } from "@/data/company";

const initial = { name: "", email: "", phone: "", message: "", website: "", privacyAccepted: false };

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [privacyError, setPrivacyError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      const message = "Vul alle velden in om verder te gaan.";
      setStatus({ type: "error", message });
      toast.error(message);
      return;
    }
    if (form.privacyAccepted !== true) {
      const message = "Bevestig dat je het privacybeleid hebt gelezen.";
      setPrivacyError(message);
      toast.error(message);
      return;
    }
    setPrivacyError("");
    setLoading(true);
    setStatus({ type: "loading", message: "Uw bericht wordt verzonden…" });
    try {
      const response = await fetch("/api/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(form),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.message || "Er ging iets mis. Probeer het later opnieuw.");
      }
      const message = result.message || "Bedankt voor uw bericht. We nemen snel contact met u op.";
      setStatus({ type: "success", message });
      toast.success(message);
      setForm(initial);
    } catch (err) {
      const message = err.message || "Er ging iets mis. Probeer het later opnieuw.";
      setStatus({ type: "error", message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-[#f7efe6] py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <div className="lg:max-w-[560px] lg:pr-8 lg:pt-4">
            <Reveal>
              <div className="mb-6 flex items-center gap-4">
                <span className="h-px w-12 bg-forest/25" />
                <span className="font-body text-xs uppercase tracking-[0.25em] text-slate2">
                  Contact
                </span>
              </div>
              <h2 className="mb-8 font-heading text-4xl font-light leading-[1.1] tracking-tight text-ink text-balance md:text-5xl lg:text-6xl">
                Klaar om te bouwen?<br />Laat het ons weten.
              </h2>
              <p className="max-w-md font-body text-[1.05rem] leading-relaxed text-slate2">
                Vertel ons over uw project. We denken graag met u mee en plannen
                een vrijblijvend gesprek in.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 space-y-3">
                <a
                  href={`tel:${company.phoneRaw}`}
                  data-testid="contact-phone"
                  className="flex items-center gap-4 rounded-2xl text-ink transition-colors duration-200 hover:text-forest"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-forest shadow-[0_10px_25px_-12px_rgba(58,79,65,0.35)]">
                    <Phone size={18} strokeWidth={1.6} />
                  </span>
                  <span className="font-body text-base">{company.phone}</span>
                </a>
                <a
                  href={`mailto:${company.email}`}
                  data-testid="contact-email"
                  className="flex items-center gap-4 rounded-2xl text-ink transition-colors duration-200 hover:text-forest"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-forest shadow-[0_10px_25px_-12px_rgba(58,79,65,0.35)]">
                    <Mail size={18} strokeWidth={1.6} />
                  </span>
                  <span className="font-body text-base">{company.email}</span>
                </a>
                <div className="flex items-center gap-4 rounded-2xl text-ink">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-forest shadow-[0_10px_25px_-12px_rgba(58,79,65,0.35)]">
                    <MapPin size={18} strokeWidth={1.6} />
                  </span>
                  <span className="font-body text-base">
                    {company.street}, {company.city}
                  </span>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="rounded-[2rem] border border-forest/10 bg-white p-7 shadow-[0_24px_80px_-32px_rgba(58,79,65,0.35)] md:p-8 lg:max-w-[610px] lg:ml-auto lg:p-10">
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div className="absolute -left-[10000px]" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} />
                </div>
                <Field label="Naam" testId="contact-name" id="contact-name" value={form.name} onChange={update("name")} />
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="E-mail" testId="contact-email-input" id="contact-email-input" type="email" value={form.email} onChange={update("email")} />
                  <Field label="Telefoon" testId="contact-phone-input" id="contact-phone-input" value={form.phone} onChange={update("phone")} />
                </div>
                <div>
                  <label htmlFor="contact-message" className="mb-2 block font-body text-sm text-slate2">Bericht</label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    className="min-h-[150px] w-full resize-none rounded-[1.25rem] border border-forest/15 bg-white px-4 py-3.5 font-body text-ink placeholder:text-slate2/60 shadow-[0_6px_20px_-12px_rgba(58,79,65,0.25)] transition-all duration-200 focus:border-forest focus:outline-none focus:ring-4 focus:ring-forest/10"
                    placeholder="Vertel ons over uw project…"
                  />
                </div>
                <div>
                  <label htmlFor="contact-privacy" className="flex cursor-pointer items-start gap-3">
                    <input
                      id="contact-privacy"
                      name="privacyAccepted"
                      type="checkbox"
                      required
                      checked={form.privacyAccepted}
                      onChange={(e) => {
                        setForm((current) => ({ ...current, privacyAccepted: e.target.checked }));
                        if (e.target.checked) setPrivacyError("");
                      }}
                      onInvalid={() => setPrivacyError("Bevestig dat je het privacybeleid hebt gelezen.")}
                      aria-invalid={privacyError ? "true" : "false"}
                      aria-describedby={privacyError ? "contact-privacy-help contact-privacy-error" : "contact-privacy-help"}
                      className="mt-1 h-5 w-5 shrink-0 cursor-pointer rounded border-forest/25 bg-white accent-forest shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                    />
                    <span className="font-body text-sm leading-relaxed text-slate2">
                      Ik heb het{" "}
                      <Link
                        to="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-sm font-medium text-forest underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                      >
                        privacybeleid
                      </Link>{" "}
                      gelezen en begrijp hoe mijn persoonsgegevens worden verwerkt.
                    </span>
                  </label>
                  <p id="contact-privacy-help" className="mt-3 pl-8 font-body text-xs leading-relaxed text-slate2/80">
                    We gebruiken je gegevens uitsluitend om je aanvraag te beantwoorden en bewaren ze niet langer dan nodig voor de opvolging van het project.
                  </p>
                  {privacyError && (
                    <p id="contact-privacy-error" role="alert" className="mt-2 pl-8 font-body text-sm text-red-600">
                      {privacyError}
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="group inline-flex w-full items-center justify-center gap-3 rounded-cta bg-forest px-8 py-4 text-sm font-medium tracking-[0.02em] text-white shadow-[0_16px_36px_-18px_rgba(58,79,65,0.8)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-forestdark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:opacity-60 disabled:hover:translate-y-0 sm:w-auto"
                >
                  {loading ? "Bezig met verzenden…" : "Verstuur bericht"}
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <p
                  role="status"
                  aria-live="polite"
                  className={`font-body text-sm ${status.type === "error" ? "text-red-600" : "text-slate2"}`}
                >
                  {status.message}
                </p>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, type = "text", testId, id }) => (
  <div>
    <label htmlFor={id} className="mb-2 block font-body text-sm text-slate2">{label}</label>
    <input
      id={id}
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      className="w-full rounded-[1.25rem] border border-forest/15 bg-white px-4 py-3.5 font-body text-ink placeholder:text-slate2/60 shadow-[0_6px_20px_-12px_rgba(58,79,65,0.25)] transition-all duration-200 focus:border-forest focus:outline-none focus:ring-4 focus:ring-forest/10"
    />
  </div>
);

export default Contact;
